import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import SignaturePad from 'signature_pad';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';

@Component({
  selector: 'tidy-signature-pad',
  templateUrl: './signature-pad.component.html',
  styleUrls: ['./signature-pad.component.scss'],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TidyUiComponentsModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class TidySignaturePadComponent implements AfterViewInit, OnDestroy {
  private hostRef = inject(ElementRef);

  @ViewChild('canvas', { static: true }) canvas!: ElementRef<HTMLCanvasElement>;

  @Input() height = 180;
  @Input() penColor = '#000000';
  @Input() backgroundColor = '#ffffff';

  @Output() signatureChange = new EventEmitter<string>();

  private pad: SignaturePad | null = null;

  ngAfterViewInit(): void {
    this.initPad();
  }

  private initPad() {
    const canvasEl = this.canvas.nativeElement;
    this.resizeCanvas();
    this.pad = new SignaturePad(canvasEl, {
      penColor: this.penColor,
      backgroundColor: this.backgroundColor,
    });
    this.pad.addEventListener('endStroke', () => this.emit());
  }

  @HostListener('window:resize')
  onWindowResize() {
    if (!this.pad) { return; }
    const dataUrl = this.pad.toDataURL();
    this.resizeCanvas();
    if (dataUrl) { this.pad.fromDataURL(dataUrl); }
  }

  private resizeCanvas() {
    const canvas = this.canvas.nativeElement;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const hostWidth = this.hostRef.nativeElement.offsetWidth || canvas.offsetWidth || 300;
    canvas.width = hostWidth * ratio;
    canvas.height = this.height * ratio;
    canvas.style.width = `${hostWidth}px`;
    canvas.style.height = `${this.height}px`;
    canvas.getContext('2d')?.scale(ratio, ratio);
  }

  clear() {
    this.pad?.clear();
    this.signatureChange.emit('');
  }

  isEmpty(): boolean {
    return this.pad?.isEmpty() ?? true;
  }

  private emit() {
    if (!this.pad || this.pad.isEmpty()) {
      this.signatureChange.emit('');
      return;
    }
    this.signatureChange.emit(this.pad.toDataURL('image/png'));
  }

  ngOnDestroy(): void {
    this.pad?.off();
    this.pad = null;
  }
}
